import { Children, isValidElement, memo, useLayoutEffect, useMemo, type FC } from 'react';
import { useMediaQuery } from 'react-responsive';

import { Media } from '../../../../../theme/Media.tsx';
import { DATA_ATTR_TABLE_HEADROW } from '../../constants.ts';
import { useTableContext } from '../../context.tsx';
import { buildGridColumns, type ColumnInput } from './columns.ts';
import { HeadRowWrapper, HeadRowInner } from './styled.ts';
import type { HeadRowProps } from './types.ts';

export const HeadRow: FC<HeadRowProps> = memo(function HeadRow({ children, className }) {
  const { inner, registerColumns } = useTableContext();
  const isMobile = useMediaQuery({ query: Media.xsMax });

  const columnMeta = useMemo(() => {
    const columns = Children.toArray(children)
      .filter(isValidElement)
      .map((child) => child.props as ColumnInput);

    return buildGridColumns(columns);
  }, [children]);

  // Register columns before the mobile early-return below, so columnCount stays correct even when
  // the header itself isn't rendered on mobile (Loader and SubRow rely on it for their layout).
  useLayoutEffect(() => {
    registerColumns(columnMeta);
  }, [columnMeta, registerColumns]);

  if (isMobile && !columnMeta.hasCheckbox) return null;

  return (
    <HeadRowWrapper className={className} $inner={inner} role='presentation' {...{ [DATA_ATTR_TABLE_HEADROW]: inner ? 'inner' : 'true' }}>
      <HeadRowInner role='row'>{children}</HeadRowInner>
    </HeadRowWrapper>
  );
});
